import { getVendorLanes, listVendors } from '@api/vendors';
import { LaneEditModal } from '@components/containers/lanes/LaneEditModal';
import {
    buildLaneColumns,
    type LaneRow,
} from '@components/containers/lanes/laneColumns';
import { DataSheet } from '@components/ui/DataSheet';
import { useCbmEnabled } from '@hooks/useCbmEnabled';
import { Button, Group, Select, Stack, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState, type FC } from 'react';

/**
 * Settings → Lanes. Where a vendor's shipping legs are read and changed.
 *
 * WHY IT IS HERE AND NOT ONLY ON SUPPLIERS. The lane editor has existed for a while, but it
 * lived on the Suppliers page inside a vendor's pane, which is not where anyone goes looking
 * for master data — every other editable table in the product is in this modal. The grid and
 * the modal are the same ones the Suppliers pane uses, imported from `lanes/`, so
 * the two screens cannot drift apart.
 *
 * A LANE BELONGS TO A VENDOR, so this asks which vendor first rather than showing a flat list
 * of everything. That is not only a UI choice: the only lane endpoints are vendor-scoped
 * (GET / POST / PATCH under `/vendors/:vendorId/lanes`), and every live lane carries a
 * vendor id — including the warehouse-to-warehouse ones — so a vendor picker reaches all of
 * them without inventing a list-all route.
 *
 * ADD AND EDIT ONLY, deliberately. There is no delete route and none is wanted yet: lead
 * times feed every expected date on a shipment, so removing a lane silently moves dates on
 * work already in flight. A wrong lane is corrected by editing it.
 *
 * EVERY VENDOR, not just manufacturers. `listVendorsForPicker` narrows to manufacturers
 * because a production order is handed to someone who makes; lanes are not that. RM suppliers
 * carry 28 of the live lanes between them, and narrowing here would hide them.
 */
const LanesTab: FC = () => {
    const [vendorId, setVendorId] = useState<string | null>(null);
    const [modal, setModal] = useState<{ open: boolean; lane: LaneRow | null }>({
        open: false,
        lane: null,
    });
    const cbmEnabled = useCbmEnabled();

    const vendorsQuery = useQuery({
        queryKey: ['settings-lanes-vendors'],
        queryFn: listVendors,
    });

    const lanesQuery = useQuery({
        queryKey: ['settings-lanes', vendorId],
        queryFn: () => getVendorLanes(vendorId as string),
        enabled: Boolean(vendorId),
    });

    const vendorOptions = useMemo(
        () =>
            (vendorsQuery.data ?? [])
                .map((v) => ({ value: v.vendorId, label: v.name || v.vendorId }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        [vendorsQuery.data],
    );

    /*
     * The grid renders an em dash for a missing port, and the modal turns that back into
     * null on the way in — the same contract the Suppliers pane uses, kept here so a lane
     * edited from either screen round-trips identically.
     */
    const rows = useMemo<LaneRow[]>(
        () =>
            (lanesQuery.data ?? []).map((l) => ({
                laneId: l.laneId,
                warehouseId: l.warehouseId,
                source: l.source ?? '—',
                destination: l.destination ?? '—',
                productionTime: l.productionTime ?? null,
                qcTime: l.qcTime ?? null,
                transitTime: l.transitTime ?? null,
                deliveryTime: l.deliveryTime ?? null,
                mode: l.mode ?? null,
                maxCbmPerShipment: l.maxCbmPerShipment ?? null,
            })),
        [lanesQuery.data],
    );

    const columns = useMemo(
        () => buildLaneColumns(cbmEnabled, (lane) => setModal({ open: true, lane })),
        [cbmEnabled],
    );

    return (
        <Stack gap="md">
            <Group justify="space-between" align="flex-end">
                <Select
                    label="Vendor"
                    placeholder="Pick a vendor to see its lanes"
                    data={vendorOptions}
                    value={vendorId}
                    onChange={setVendorId}
                    searchable
                    w={320}
                    disabled={vendorsQuery.isLoading}
                />
                <Button
                    size="xs"
                    disabled={!vendorId}
                    onClick={() => setModal({ open: true, lane: null })}
                >
                    Add lane
                </Button>
            </Group>

            {!vendorId ? (
                <Text size="sm" c="dimmed">
                    Pick a vendor to see and edit its lanes.
                </Text>
            ) : (
                <>
                    <DataSheet<LaneRow>
                        data={rows}
                        columns={columns}
                        isLoading={lanesQuery.isLoading}
                        getRowKey={(row, idx) => row.laneId ?? String(idx)}
                        emptyState="No lanes for this vendor yet. Add one to set its lead times."
                        bare
                        hideHeaderBar
                    />
                    <Text size="xs" c="dimmed">
                        Production, QC, transit and delivery days add up to the
                        door-to-door lead time an expected date is seeded from.
                    </Text>
                </>
            )}

            <LaneEditModal
                opened={modal.open}
                vendorId={vendorId}
                lane={modal.lane}
                cbmEnabled={cbmEnabled}
                onClose={() => setModal({ open: false, lane: null })}
                onSaved={() => void lanesQuery.refetch()}
            />
        </Stack>
    );
};

export default LanesTab;
